import { RotateCw } from 'lucide-react';
import { EntityModal, StatusBadge } from '../common';
import { useDialog } from '../../context/DialogContext';
import { useMailLogEntry, useResendMail } from '../../hooks/useMailLog';
import type { MailLogScope } from '../../api/mailLog';
import {
  DELIVERY_COLOR,
  DELIVERY_LABEL,
  STATUS_COLOR,
  STATUS_HINT,
  STATUS_LABEL,
  categoryLabel,
  formatWhen,
} from './mailLogFormat';

export interface MailLogDetailProps {
  /** The message to show, or null when closed. */
  id: string | null;
  scope: MailLogScope;
  /** Offer "Send again" (producers). */
  canResend: boolean;
  onClose: () => void;
}

/**
 * One logged message: who it went to, what became of it, and what it said —
 * the answer to "did they ever get the invitation?", with the text.
 */
export function MailLogDetail({ id, scope, canResend, onClose }: MailLogDetailProps) {
  const { data: entry, isLoading, isError } = useMailLogEntry(id, scope);
  const resend = useResendMail(scope);
  const { confirm, alert } = useDialog();

  async function sendAgain() {
    if (!entry) return;
    const ok = await confirm({
      title: 'Send this again?',
      message: `The same message goes to ${entry.to.join(', ')}. Attachments are not kept, so a copy sent again carries none, and addresses that have bounced are left off.`,
      confirmLabel: 'Send again',
      variant: 'warning',
    });
    if (!ok) return;
    try {
      const result = await resend.mutateAsync(entry.id);
      await alert({
        title: result.status === 'sent' ? 'Sent again' : STATUS_LABEL[result.status],
        message: result.error ?? STATUS_HINT[result.status],
        variant:
          result.status === 'sent' ? 'success' : result.status === 'failed' ? 'error' : 'info',
      });
    } catch (err) {
      await alert({
        title: 'It could not be sent again',
        message: err instanceof Error ? err.message : 'Please try again.',
        variant: 'error',
      });
    }
  }

  return (
    <EntityModal
      open={id !== null}
      onClose={onClose}
      title={entry?.subject || 'Message'}
      size="lg"
      footer={
        canResend && entry ? (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={sendAgain}
              disabled={resend.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              <RotateCw size={14} />
              Send again
            </button>
          </div>
        ) : undefined
      }
    >
      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      {isError && (
        <p className="text-sm text-red-600" role="alert">
          This message could not be loaded.
        </p>
      )}
      {entry && (
        <div className="space-y-3" data-testid="mail-log-detail">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            <div>
              <dt className="text-gray-500">To</dt>
              <dd className="text-gray-900 break-all">
                {entry.guestName ? `${entry.guestName} · ` : ''}
                {entry.to.join(', ')}
                {entry.cc.length > 0 && (
                  <span className="block text-xs text-gray-500">Cc {entry.cc.join(', ')}</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Sent</dt>
              <dd className="text-gray-900">{formatWhen(entry.sentAt ?? entry.createdAt)}</dd>
            </div>
            <div>
              <dt className="text-gray-500">What</dt>
              <dd className="text-gray-900">
                {categoryLabel(entry.category)} ·{' '}
                {entry.channel === 'sms' ? 'Text message' : 'Email'}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Status</dt>
              <dd className="flex flex-wrap items-center gap-1.5" title={STATUS_HINT[entry.status]}>
                <StatusBadge
                  label={STATUS_LABEL[entry.status]}
                  color={STATUS_COLOR[entry.status]}
                />
                {entry.delivery && (
                  <StatusBadge
                    label={`${DELIVERY_LABEL[entry.delivery]} ${formatWhen(entry.deliveryAt)}`}
                    color={DELIVERY_COLOR[entry.delivery]}
                  />
                )}
              </dd>
            </div>
            {entry.replyTo && (
              <div className="sm:col-span-2">
                <dt className="text-gray-500">Replies go to</dt>
                <dd className="text-gray-900 break-all">{entry.replyTo}</dd>
              </div>
            )}
          </dl>
          {entry.deliveryDetail && (
            <p className="text-sm text-gray-600">
              <span className="text-gray-500">The mail service said: </span>
              {entry.deliveryDetail}
            </p>
          )}
          {entry.error && (
            <p
              className={`text-sm ${entry.status === 'sent' ? 'text-amber-700' : 'text-red-600'}`}
              role="alert"
            >
              {entry.error}
            </p>
          )}
          {entry.channel === 'sms' || entry.bodyHtml === '' ? (
            <pre className="whitespace-pre-wrap text-sm bg-gray-50 border border-gray-200 rounded-lg p-3">
              {entry.bodyText || '(no text)'}
            </pre>
          ) : (
            // Rendered in a sandboxed frame: this view must never be a way for
            // message content to run script in the app.
            <iframe
              title="Message preview"
              sandbox=""
              srcDoc={entry.bodyHtml}
              className="theme-light w-full h-96 border border-gray-200 rounded-lg bg-white"
            />
          )}
        </div>
      )}
    </EntityModal>
  );
}
